import { ScrollArea, Separator } from 'shared-ui';

import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';
import { ToggleRow } from '@/components/shared/ToggleRow';

import type { BasemapDefinition } from '../utils/basemaps';
import { resolveBasemap } from '../utils/basemaps';
import type {
    EntityToggleState,
    FeatureOverlayDef,
    FeatureOverlayKey,
    FeatureOverlayState,
    MapMode,
    PoiTypeKey,
    PoiTypeState,
} from '../utils/mapLayers';
import { ENTITY_TOGGLES, getFeatureOverlaysForMode, POI_TYPES } from '../utils/mapLayers';
import { MapStyleThumbnail } from './MapStyleThumbnail';

export interface MapLayersPanelProps {
    isOpen: boolean;
    mode: MapMode;
    basemaps: BasemapDefinition[];
    selectedBasemapId: string;
    onBasemapChange: (id: string) => void;
    onClose: () => void;
    featureOverlays: FeatureOverlayState;
    onFeatureOverlayChange: (key: FeatureOverlayKey, enabled: boolean) => void;
    poiTypes: PoiTypeState;
    onPoiTypeChange: (key: PoiTypeKey, enabled: boolean) => void;
    /**
     * The split-model entity toggles. When provided, the per-status POI section is
     * replaced by the two entity rows (POIs / Leaks) — per-status control lives solely in
     * the Filters panel under the entity model. Absent = pre-split behaviour.
     */
    entityToggles?: EntityToggleState;
    onEntityToggleChange?: (key: keyof EntityToggleState, enabled: boolean) => void;
    /** High-risk air-valve count, appended to the SAVI "High Risk Air Valves" row label. */
    highRiskCount: number | null;
    /** Per-user vehicle-tracking capability; without it the Technician Vans row is absent. */
    canViewVehicleTracking: boolean;
}

/** SAVI shows the high-risk count in its Layers row label, e.g. "High Risk Air Valves (67)". */
function featureOverlayLabel(def: FeatureOverlayDef, highRiskCount: number | null): string {
    if (def.key === 'highRiskAirValves' && highRiskCount != null) {
        return `${def.label} (${String(highRiskCount)})`;
    }
    return def.label;
}

function PanelContent({
    mode,
    basemaps,
    selectedBasemapId,
    onBasemapChange,
    onClose,
    featureOverlays,
    onFeatureOverlayChange,
    poiTypes,
    onPoiTypeChange,
    entityToggles,
    onEntityToggleChange,
    highRiskCount,
    canViewVehicleTracking,
}: Omit<MapLayersPanelProps, 'isOpen'>) {
    const selectedBasemap = resolveBasemap(basemaps, selectedBasemapId);
    const overlayDefs = getFeatureOverlaysForMode(mode, canViewVehicleTracking);
    const isSavi = mode === 'savi';

    return (
        <>
            <div className="flex h-20 shrink-0 items-center justify-between px-6">
                <h2 className="text-lg leading-7 font-black tracking-normal uppercase md:text-2xl md:leading-8">
                    Layers
                </h2>
                <PanelCloseButton onClick={onClose} aria-label="Close layers panel" />
            </div>
            <Separator />
            <ScrollArea className="min-h-0 flex-1">
                <div className="px-6 py-4">
                    <p className="mb-3 text-sm font-medium">
                        Map Style <span>— {selectedBasemap.label}</span>
                    </p>
                    <div className="flex gap-1">
                        {basemaps.map((b) => (
                            <MapStyleThumbnail
                                key={b.id}
                                basemap={b}
                                isSelected={b.id === selectedBasemapId}
                                onSelect={onBasemapChange}
                            />
                        ))}
                    </div>
                </div>
                <div className="px-6 pt-3 pb-3">
                    <p className="text-foreground mb-2 text-sm leading-5 font-medium">Features</p>
                    {overlayDefs.map((def) => (
                        <ToggleRow
                            key={def.key}
                            label={featureOverlayLabel(def, highRiskCount)}
                            checked={featureOverlays[def.key]}
                            onCheckedChange={(enabled) => {
                                onFeatureOverlayChange(def.key, enabled);
                            }}
                        />
                    ))}
                </div>
                {/* Entity/POI section is Find & Fix only; SAVI organises its valves under Features.
                    Under the split model the section is the two entity rows — per-status control
                    lives solely in the Filters panel. */}
                {!isSavi && entityToggles && onEntityToggleChange && (
                    <div className="px-6 pt-3 pb-4">
                        <p className="text-foreground mb-2 text-sm leading-5 font-medium">
                            Points of Interest &amp; Leaks
                        </p>
                        {ENTITY_TOGGLES.map((def) => (
                            <ToggleRow
                                key={def.key}
                                label={def.label}
                                checked={entityToggles[def.key]}
                                onCheckedChange={(enabled) => {
                                    onEntityToggleChange(def.key, enabled);
                                }}
                            />
                        ))}
                    </div>
                )}
                {!isSavi && !entityToggles && (
                    <div className="px-6 pt-3 pb-4">
                        <p className="text-foreground mb-2 text-sm leading-5 font-medium">Points of Interest (POI)</p>
                        {POI_TYPES.map((def) => (
                            <ToggleRow
                                key={def.key}
                                label={def.label}
                                checked={poiTypes[def.key]}
                                onCheckedChange={(enabled) => {
                                    onPoiTypeChange(def.key, enabled);
                                }}
                            />
                        ))}
                    </div>
                )}
            </ScrollArea>
        </>
    );
}

export function MapLayersPanel(props: MapLayersPanelProps) {
    return (
        <SlidingPanel isOpen={props.isOpen} onClose={props.onClose} ariaLabel="Map layers" title="Layers">
            <PanelContent {...props} />
        </SlidingPanel>
    );
}
